import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Row, Col } from 'react-bootstrap';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import { entityMetaMapping, pimModule, intlModule, clientModule, MetaForm } from '@chedri/base';
import { getSalutationOptions } from 'helpers/clients';
import Loader from 'components/layout/Loader';
import FormAutocomplete from 'components/forms/FormAutocomplete';
import FormInput from 'components/forms/FormInput';
import PhoneNumberInput from './PhoneNumberInput';
import AddressInput from './AddressInput';

ContactForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  contact: PropTypes.object,
  onSuccess: PropTypes.func,
  validate: PropTypes.func,
  reload: PropTypes.func,
};

ContactForm.defaultProps = {
  contact: {},
  onSuccess: () => undefined,
  validate: () => true,
  reload: () => {},
};

const contactMetaId = entityMetaMapping.contact;

function ContactForm({ contact: origin, onSuccess, validate, reload }) {
  const dispatch = useDispatch();

  const messages = useSelector(intlModule.selectors.getMessages);
  const lang = useSelector(intlModule.selectors.getEditLanguage);
  const contactData = useSelector(pimModule.contacts.selectors.getData);
  const defaultClient = useSelector(pimModule.defaultClient.selectors.getData);
  const targetData = useSelector(clientModule.client.selectors.getData);

  const [salutationOptions, setSalutationOptions] = useState([]);
  const [target, setTarget] = useState(null);
  const [data, setData] = useState(origin);
  const [submitted, setSubmitted] = useState(false);
  const [addressErrors, setAddressErrors] = useState({});
  const [numberErrors, setNumberErrors] = useState({});

  const storeData = useCallback(
    errors => {
      setSubmitted(true);
      if (!_isEmpty(errors)) {
        return;
      }
      if (!_isEmpty(addressErrors)) {
        return;
      }
      if (!_isEmpty(numberErrors)) {
        return;
      }

      if (!validate(data)) {
        return;
      }

      const addresses = _map(data && data.addresses, address => ({ ...address, target }));

      if (origin && origin.id) {
        dispatch(pimModule.contacts.update({ ...data, addresses, target }));
      } else {
        dispatch(pimModule.contacts.create({ ...data, addresses, target }));
      }
    },
    [data, target, addressErrors, numberErrors]
  );

  useEffect(() => {
    dispatch(pimModule.contacts.clearData());
    setSalutationOptions(getSalutationOptions(messages));
    dispatch(pimModule.defaultClient.fetchData());
  }, []);

  useEffect(() => {
    if (!_isEmpty(contactData)) {
      dispatch(pimModule.contacts.clearData());
      onSuccess(contactData);
      reload();
    }
  }, [contactData]);

  useEffect(() => {
    if (origin && origin.target) {
      dispatch(clientModule.client.fetchData({ id: origin.target }));
    }
  }, [origin]);

  useEffect(() => {
    if (!target && _isEmpty(origin)) {
      setTarget(convertClientToMyCompany(defaultClient));
    }
    if (!target && targetData) {
      setTarget(targetData);
    }
  }, [target, targetData, defaultClient]);

  useEffect(() => {
    return () => {
      // Dispose ContactForm
      dispatch(clientModule.client.clearData());
    };
  }, []);

  const convertClientToMyCompany = client => {
    if (!client) {
      return client;
    }
    return {
      ...client,
      profile: {
        ...client.profile,
        company: `${messages.my_company}${defaultClient.profile.company ? ' - ' + defaultClient.profile.company : ''}`,
      },
    };
  };

  /**
   * gender
   */
  return (
    <MetaForm metaId={contactMetaId} language={lang} source={data} onChange={setData} client={target}>
      {metaContext => {
        const { getProperty, setProperty, errors, isLoading, validate, transfer } = metaContext;
        const { client } = transfer; // Need to be done to re-render

        return isLoading || !client ? (
          <Loader />
        ) : (
          <div className="contact-form">
            <div className="form-group-attached">
              <FormAutocomplete
                label={messages.salutation}
                options={salutationOptions}
                value={getProperty('salutation')}
                onChange={setProperty('salutation')}
                validation={submitted && errors ? errors.salutation : undefined}
              />
              <FormInput
                type="text"
                label={messages.title}
                value={getProperty('title')}
                onChange={setProperty('title')}
                validation={submitted && errors ? errors.title : undefined}
              />
              <FormInput
                type="text"
                label={messages.first_name}
                value={getProperty('firstName')}
                onChange={setProperty('firstName')}
                validation={submitted && errors ? errors.firstName : undefined}
              />
              <FormInput
                type="text"
                label={messages.last_name}
                value={getProperty('lastName')}
                onChange={setProperty('lastName')}
                validation={submitted && errors ? errors.lastName : undefined}
              />
              <FormInput
                type="text"
                label={messages.job_title}
                value={getProperty('jobTitle')}
                onChange={setProperty('jobTitle')}
                validation={submitted && errors ? errors.jobTitle : undefined}
              />
            </div>
            <div className="form-group-attached m-t-10">
              <FormInput
                type="text"
                label={messages.email}
                value={getProperty('email')}
                onChange={setProperty('email')}
                validation={submitted && errors ? errors.email : undefined}
              />
              <FormInput
                type="text"
                label={messages.company}
                value={getProperty('company')}
                onChange={setProperty('company')}
                validation={submitted && errors ? errors.company : undefined}
              />
              <FormInput
                type="text"
                label={messages.website}
                value={getProperty('website')}
                onChange={setProperty('website')}
                validation={submitted && errors ? errors.website : undefined}
              />
            </div>
            <div className="form-group-attached m-t-10">
              <PhoneNumberInput
                value={getProperty('numbers')}
                submitted={submitted}
                onError={newNumberErrors => setNumberErrors(newNumberErrors)}
                onChange={setProperty('numbers')}
              />
            </div>
            <div className="form-group-attached m-t-10">
              <AddressInput
                value={getProperty('addresses')}
                submitted={submitted}
                onError={newAddressErrors => setAddressErrors(newAddressErrors)}
                onChange={setProperty('addresses')}
              />
            </div>
            <div className="form-group-attached m-t-10">
              <Row>
                <Col>
                  <Button
                    bsStyle="primary"
                    className="pull-right"
                    onClick={() => validate() || setTimeout(() => storeData(errors))}
                  >
                    <i className="fa fa-save" /> {messages.save}
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
        );
      }}
    </MetaForm>
  );
}

export default ContactForm;
